import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Link as RouterLink } from '~/lib/react-router-dom';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useAgentHubUserRowsDetailQueries } from '~/hooks/apis/agent/useAgentHubUserRowsDetailQueries';
import { useListAgencies } from '~/hooks/apis/agent/useListAgencies';
import { useListAgentHubUsers } from '~/hooks/apis/agent/useListAgentHubUsers';

import { resolveAgentHubAdminDisplayFlags } from '~/utils/agentHub';

import AgencyDetailsModal from '../Agency/AgencyDetails/AgencyDetailsModal';

const PAGE_SIZE = 10;

interface Props {
  affiliationId: string;
  affiliationName: string;
  affiliationRegionLabel: string;
  isManagedAffiliation: boolean;
  onClose: () => void;
  opened: boolean;
  summaryLoading?: boolean;
  summaryError?: boolean;
}

export default function AffiliationShowModal({
  affiliationId,
  affiliationName,
  affiliationRegionLabel,
  isManagedAffiliation,
  onClose,
  opened,
  summaryLoading = false,
  summaryError = false,
}: Props) {
  const [agencyPage, setAgencyPage] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [agencySearchInput, setAgencySearchInput] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedAgencyId, setSelectedAgencyId] = useState<string | null>(null);
  const [agencyDetailsOpen, setAgencyDetailsOpen] = useState(false);

  const yesLabel = 'Yes';
  const noLabel = 'No';

  useEffect(() => {
    if (!opened) {
      return;
    }

    setAgencyPage(0);
    setUserPage(0);
    setAgencySearchInput('');
    setAgencySearch('');
    setUserSearchInput('');
    setUserSearch('');
    setSelectedAgencyId(null);
    setAgencyDetailsOpen(false);
  }, [affiliationId, opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    const handle = setTimeout(() => setAgencySearch(agencySearchInput), 400);
    return () => clearTimeout(handle);
  }, [agencySearchInput, opened]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    const handle = setTimeout(() => setUserSearch(userSearchInput), 400);
    return () => clearTimeout(handle);
  }, [opened, userSearchInput]);

  const {
    data: { rows: agencyRows = [], total: agencyTotal = 0 } = {},
    isFetching: loadingAgencies,
    refetch: refetchAgencies,
  } = useListAgencies(
    {
      affiliationId,
      page: agencyPage.toString(),
      pageSize: PAGE_SIZE.toString(),
      ...(agencySearch.trim() ? { search: agencySearch.trim() } : {}),
    },
    { enabled: opened && !!affiliationId },
  );

  const { data: { rows: userRows = [], total: userTotal = 0 } = {}, isFetching: loadingUsers } = useListAgentHubUsers(
    {
      affiliationId,
      page: userPage.toString(),
      pageSize: PAGE_SIZE.toString(),
      ...(userSearch.trim() ? { search: userSearch.trim() } : {}),
    },
    { enabled: opened && !!affiliationId },
  );

  const { queryByRowId, isDetailPending } = useAgentHubUserRowsDetailQueries(userRows, {
    enabled: opened && !!affiliationId,
  });

  const openAgencyDetails = useCallback((id: string) => {
    setSelectedAgencyId(id);
    setAgencyDetailsOpen(true);
  }, []);

  const closeAgencyDetails = useCallback(() => {
    setAgencyDetailsOpen(false);
    setSelectedAgencyId(null);
  }, []);

  const agencyColumns: Array<GridColDef> = useMemo(() => {
    return [
      { field: 'name', headerName: 'Name', flex: 1.2, sortable: false, display: 'flex' },
      { field: 'city', headerName: 'City', flex: 1, sortable: false, display: 'flex' },
      { field: 'affiliation', headerName: 'Affiliation', flex: 1, sortable: false, display: 'flex' },
      {
        field: 'actions',
        headerName: '',
        flex: 0.6,
        sortable: false,
        display: 'flex',
        renderCell: (params) => (
          <Button variant="text" onClick={() => openAgencyDetails(params.row.id)}>
            Show
          </Button>
        ),
      },
    ];
  }, [openAgencyDetails]);

  const userColumns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      sortable: false,
      display: 'flex',
      renderCell: (params) => {
        const nameText = params.row.name || '—';
        const profileId = params.row.customerId ? params.row.customerId : null;

        if (!profileId) {
          return nameText;
        }

        return (
          <Link component={RouterLink} to={`/users/${profileId}`} target="_blank" rel="noreferrer">
            {nameText}
          </Link>
        );
      },
    },
    { field: 'agencyName', headerName: 'Agency', flex: 1, sortable: false, display: 'flex' },
    {
      field: 'isAgencyAdmin',
      headerName: 'Agency admin',
      flex: 0.8,
      sortable: false,
      display: 'flex',
      renderCell: (params) => {
        const q = queryByRowId.get(params.row.id);
        if (!q || q.isPending) {
          return '…';
        }
        if (q.isError) {
          return '—';
        }
        const flags = resolveAgentHubAdminDisplayFlags(q.data);
        return flags.isAgencyAdmin ? yesLabel : noLabel;
      },
    },
    {
      field: 'isAffiliationAdmin',
      headerName: 'Affiliation admin',
      flex: 0.8,
      sortable: false,
      display: 'flex',
      renderCell: (params) => {
        const q = queryByRowId.get(params.row.id);
        if (!q || q.isPending) {
          return '…';
        }
        if (q.isError) {
          return '—';
        }
        const flags = resolveAgentHubAdminDisplayFlags(q.data);
        return flags.isAffiliationAdmin ? yesLabel : noLabel;
      },
    },
  ];

  const onAgencySearchChange = useCallback((value: string) => {
    setAgencySearchInput(value);
    setAgencyPage(0);
  }, []);

  const onUserSearchChange = useCallback((value: string) => {
    setUserSearchInput(value);
    setUserPage(0);
  }, []);

  return (
    <>
      <Dialog fullWidth maxWidth="lg" open={opened} onClose={onClose}>
        <DialogTitle>Affiliation</DialogTitle>
        <DialogContent>
          <Stack spacing={0.5} sx={{ pt: 0, pb: 2 }}>
            {summaryError && !summaryLoading ? (
              <Typography variant="body2" color="error">
                Could not load affiliation details. Agency and member lists below still use this affiliation.
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Name{' '}
              </Box>
              {summaryLoading ? '…' : summaryError ? '—' : affiliationName || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Region{' '}
              </Box>
              {summaryLoading ? '…' : summaryError ? '—' : affiliationRegionLabel || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Managed affiliation{' '}
              </Box>
              {summaryLoading ? '…' : summaryError ? '—' : isManagedAffiliation ? 'Yes' : 'No'}
            </Typography>
          </Stack>

          <Stack spacing={4} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Agencies
              </Typography>
              <Stack mb={1} maxWidth={400}>
                <TextField
                  fullWidth
                  label="Search agencies"
                  placeholder="Search agencies"
                  size="small"
                  value={agencySearchInput}
                  onChange={(e) => onAgencySearchChange(e.target.value)}
                />
              </Stack>
              <DataGrid
                autoHeight
                rows={agencyRows}
                rowCount={agencyTotal}
                columns={agencyColumns}
                loading={loadingAgencies}
                pagination
                paginationMode="server"
                paginationModel={{ page: agencyPage, pageSize: PAGE_SIZE }}
                pageSizeOptions={[PAGE_SIZE]}
                onPaginationModelChange={({ page }) => setAgencyPage(page)}
                slots={{ pagination: GridPagination }}
                disableColumnFilter
                disableColumnMenu
                disableRowSelectionOnClick
              />
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Agents
              </Typography>
              <Stack mb={1} maxWidth={400}>
                <TextField
                  fullWidth
                  label="Search agents"
                  placeholder="Search agents"
                  size="small"
                  value={userSearchInput}
                  onChange={(e) => onUserSearchChange(e.target.value)}
                />
              </Stack>
              <DataGrid
                autoHeight
                rows={userRows}
                rowCount={userTotal}
                columns={userColumns}
                loading={loadingUsers || isDetailPending}
                pagination
                paginationMode="server"
                paginationModel={{ page: userPage, pageSize: PAGE_SIZE }}
                pageSizeOptions={[PAGE_SIZE]}
                onPaginationModelChange={({ page }) => setUserPage(page)}
                slots={{ pagination: GridPagination }}
                disableColumnFilter
                disableColumnMenu
                disableRowSelectionOnClick
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <AgencyDetailsModal
        id={selectedAgencyId ?? ''}
        opened={agencyDetailsOpen}
        onClose={closeAgencyDetails}
        onUpdate={() => {
          refetchAgencies();
        }}
      />
    </>
  );
}
